export enum TextureLevel {
  ST1,
  MT2,
  MO3,
  LQ3,
  EX4,
  PU4,
  MM5,
  SB6,
  EC7
}

export enum WizardStepAnswerResult {
  Proceed,
  MissingTools,
  TestSuccess,
  TestFail
}

export interface IWizardStepAnswer {
  label: string
  result: WizardStepAnswerResult
  nextStepIndex?: number
}

interface IWizardStepImage {
  src: string
  alt: string
}

export interface IWizardStep {
  question: string
  answers: IWizardStepAnswer[]
  image?: IWizardStepImage
}

interface IWizardDefinition {
  paths: Map<TextureLevel, IWizardStep[]>
}

export const textureWizardDefinition: IWizardDefinition = {
  paths: new Map([
    [
      TextureLevel.ST1,
      [
        {
          question: "Is the fluid smooth with no 'bits' (e.g. lumps, fibres, shell, skin, husks, bone, or gristle?)",
          answers: [
            { label: "Yes", result: WizardStepAnswerResult.Proceed, nextStepIndex: 1 },
            { label: "No", result: WizardStepAnswerResult.TestFail }
          ]
        },
        {
          question: "Do you have access to a 10mL syringe (it should measure 61.5mm from 0-10mL lines)?",
          answers: [
            { label: "Yes", result: WizardStepAnswerResult.Proceed, nextStepIndex: 2 },
            { label: "No", result: WizardStepAnswerResult.MissingTools }
          ]
        },
        {
          question:
            "Fill the syringe with 10mL of the fluid and allow it to flow for 10 seconds. How much fluid remains?",
          answers: [
            { label: "Less than 1mL", result: WizardStepAnswerResult.TestFail },
            { label: "Between 1-4mL", result: WizardStepAnswerResult.TestSuccess },
            { label: "More than 4mL", result: WizardStepAnswerResult.TestFail }
          ]
        }
      ]
    ],
    [
      TextureLevel.MT2,
      [
        {
          question: "Is the fluid smooth with no 'bits' (e.g. lumps, fibres, shell, skin, husks, bone, or gristle?)",
          answers: [
            { label: "Yes", result: WizardStepAnswerResult.Proceed, nextStepIndex: 1 },
            { label: "No", result: WizardStepAnswerResult.TestFail }
          ]
        },
        {
          question: "Do you have access to a 10mL syringe (it should measure 61.5mm from 0-10mL lines)?",
          answers: [
            { label: "Yes", result: WizardStepAnswerResult.Proceed, nextStepIndex: 2 },
            { label: "No", result: WizardStepAnswerResult.MissingTools }
          ]
        },
        {
          question:
            "Fill the syringe with 10mL of the fluid and allow it to flow for 10 seconds. How much fluid remains?",
          answers: [
            { label: "Less than 4mL", result: WizardStepAnswerResult.TestFail },
            { label: "Between 4-8mL", result: WizardStepAnswerResult.TestSuccess },
            { label: "More than 8mL", result: WizardStepAnswerResult.TestFail }
          ]
        }
      ]
    ],
    [
      TextureLevel.MO3,
      [
        {
          question: "Is the fluid smooth with no 'bits' (e.g. lumps, fibres, shell, skin, husks, bone, or gristle?)",
          answers: [
            { label: "Yes", result: WizardStepAnswerResult.Proceed, nextStepIndex: 1 },
            { label: "No", result: WizardStepAnswerResult.TestFail }
          ]
        },
        {
          question: "Do you have access to a 10mL syringe (it should measure 61.5mm from 0-10mL lines)?",
          answers: [
            { label: "Yes", result: WizardStepAnswerResult.Proceed, nextStepIndex: 2 },
            { label: "No", result: WizardStepAnswerResult.MissingTools }
          ]
        },
        {
          question:
            "Fill the syringe with 10mL of the fluid and allow it to flow for 10 seconds. How much fluid remains?",
          answers: [
            { label: "Less than 8mL", result: WizardStepAnswerResult.TestFail },
            { label: "More than 8mL", result: WizardStepAnswerResult.Proceed, nextStepIndex: 3 }
          ]
        },
        {
          question: "Does the fluid drip slowly in dollops through the prongs of a fork?",
          answers: [
            { label: "Yes", result: WizardStepAnswerResult.TestSuccess },
            { label: "No", result: WizardStepAnswerResult.TestFail }
          ]
        }
      ]
    ],
    [
      TextureLevel.LQ3,
      [
        {
          question: "Is the fluid smooth with no 'bits' (e.g. lumps, fibres, shell, skin, husks, bone, or gristle?)",
          answers: [
            { label: "Yes", result: WizardStepAnswerResult.Proceed, nextStepIndex: 1 },
            { label: "No", result: WizardStepAnswerResult.TestFail }
          ]
        },
        {
          question: "Do you have access to a 10mL syringe (it should measure 61.5mm from 0-10mL lines)?",
          answers: [
            { label: "Yes", result: WizardStepAnswerResult.Proceed, nextStepIndex: 2 },
            { label: "No", result: WizardStepAnswerResult.MissingTools }
          ]
        },
        {
          question:
            "Fill the syringe with 10mL of the fluid and allow it to flow for 10 seconds. How much fluid remains?",
          answers: [
            { label: "Less than 8mL", result: WizardStepAnswerResult.TestFail },
            { label: "More than 8mL", result: WizardStepAnswerResult.Proceed, nextStepIndex: 3 }
          ]
        },
        {
          question: "Does the fluid drip slowly in dollops through the prongs of a fork?",
          answers: [
            { label: "Yes", result: WizardStepAnswerResult.TestSuccess },
            { label: "No", result: WizardStepAnswerResult.TestFail }
          ]
        }
      ]
    ],
    [
      TextureLevel.EX4,
      [
        {
          question: "What's the appearance of the food?",
          answers: [
            { label: "Smooth", result: WizardStepAnswerResult.Proceed, nextStepIndex: 1 },
            { label: "Lumpy", result: WizardStepAnswerResult.TestFail }
          ]
        },
        {
          question: "Do you have access to a metal dinner fork <strong>and</strong> a teaspoon?",
          answers: [
            { label: "Yes", result: WizardStepAnswerResult.Proceed, nextStepIndex: 2 },
            { label: "No", result: WizardStepAnswerResult.Proceed, nextStepIndex: 6 }
          ]
        },
        {
          question:
            "Does the food sit in a mound above the dinner fork? (a small amount may form a tail below the dinner fork)",
          answers: [
            { label: "Yes", result: WizardStepAnswerResult.Proceed, nextStepIndex: 3 },
            { label: "No", result: WizardStepAnswerResult.TestFail }
          ]
        },
        {
          question: "Does the food stay on the fork without dripping or flowing continuously through the fork?",
          answers: [
            { label: "Yes", result: WizardStepAnswerResult.Proceed, nextStepIndex: 4 },
            { label: "No", result: WizardStepAnswerResult.TestFail }
          ]
        },
        {
          question: "Does the food hold its shape on the teaspoon?",
          answers: [
            { label: "Yes", result: WizardStepAnswerResult.Proceed, nextStepIndex: 5 },
            { label: "No", result: WizardStepAnswerResult.TestFail }
          ]
        },
        {
          question:
            "Tilt the spoon to its side and give it a gentle flick from the wrist. Does the food slide off with little left on teaspoon (i.e. not sticky)?",
          answers: [
            { label: "Yes", result: WizardStepAnswerResult.TestSuccess },
            { label: "No", result: WizardStepAnswerResult.TestFail }
          ]
        },
        {
          question: "Are you able to hold a sample on your fingers without it dripping through continuously?",
          answers: [
            { label: "Yes", result: WizardStepAnswerResult.Proceed, nextStepIndex: 7 },
            { label: "No", result: WizardStepAnswerResult.TestFail }
          ]
        },
        {
          question: "Does the food slide smoothly and easily between fingers (i.e. not sticky?)",
          answers: [
            { label: "Yes", result: WizardStepAnswerResult.TestSuccess },
            { label: "No", result: WizardStepAnswerResult.TestFail }
          ]
        }
      ]
    ],
    [
      TextureLevel.PU4,
      [
        {
          question: "What's the appearance of the food?",
          answers: [
            { label: "Smooth", result: WizardStepAnswerResult.Proceed, nextStepIndex: 1 },
            { label: "Lumpy", result: WizardStepAnswerResult.TestFail }
          ]
        },
        {
          question: "Do you have access to a metal dinner fork <strong>and</strong> a teaspoon?",
          answers: [
            { label: "Yes", result: WizardStepAnswerResult.Proceed, nextStepIndex: 2 },
            { label: "No", result: WizardStepAnswerResult.Proceed, nextStepIndex: 6 }
          ]
        },
        {
          question:
            "Does the food sit in a mound above the dinner fork? (a small amount may form a tail below the dinner fork)",
          answers: [
            { label: "Yes", result: WizardStepAnswerResult.Proceed, nextStepIndex: 3 },
            { label: "No", result: WizardStepAnswerResult.TestFail }
          ]
        },
        {
          question: "Does the food stay on the fork without dripping or flowing continuously through the fork?",
          answers: [
            { label: "Yes", result: WizardStepAnswerResult.Proceed, nextStepIndex: 4 },
            { label: "No", result: WizardStepAnswerResult.TestFail }
          ]
        },
        {
          question: "Does the food hold its shape on the teaspoon?",
          answers: [
            { label: "Yes", result: WizardStepAnswerResult.Proceed, nextStepIndex: 5 },
            { label: "No", result: WizardStepAnswerResult.TestFail }
          ]
        },
        {
          question:
            "Tilt the spoon to its side and give it a gentle flick from the wrist. Does the food slide off with little left on teaspoon (i.e. not sticky)?",
          answers: [
            { label: "Yes", result: WizardStepAnswerResult.TestSuccess },
            { label: "No", result: WizardStepAnswerResult.TestFail }
          ]
        },
        {
          question: "Are you able to hold a sample on your fingers without it dripping through continuously?",
          answers: [
            { label: "Yes", result: WizardStepAnswerResult.Proceed, nextStepIndex: 7 },
            { label: "No", result: WizardStepAnswerResult.TestFail }
          ]
        },
        {
          question: "Does the food slide smoothly and easily between fingers (i.e. not sticky?)",
          answers: [
            { label: "Yes", result: WizardStepAnswerResult.TestSuccess },
            { label: "No", result: WizardStepAnswerResult.TestFail }
          ]
        }
      ]
    ],
    [
      TextureLevel.MM5,
      [
        {
          question: "Does the food have lumps that are 4mm or smaller?",
          answers: [
            { label: "Yes", result: WizardStepAnswerResult.Proceed, nextStepIndex: 1 },
            { label: "No", result: WizardStepAnswerResult.TestFail }
          ]
        },
        {
          question: "Does the food hold together without a thin liquid pooling?",
          answers: [
            { label: "Yes", result: WizardStepAnswerResult.Proceed, nextStepIndex: 2 },
            { label: "No", result: WizardStepAnswerResult.TestFail }
          ]
        },
        {
          question: "Do you have access to a metal dinner fork <strong>and</strong> a teaspoon?",
          answers: [
            { label: "Yes", result: WizardStepAnswerResult.Proceed, nextStepIndex: 3 },
            { label: "No", result: WizardStepAnswerResult.Proceed, nextStepIndex: 7 }
          ]
        },
        {
          question:
            "Can the food be easily mashed with little pressure from a dinner fork? (pressure should not make thumb nail blanch to white)",
          answers: [
            { label: "Yes", result: WizardStepAnswerResult.Proceed, nextStepIndex: 4 },
            { label: "No", result: WizardStepAnswerResult.TestFail }
          ]
        },
        {
          question: "Does the food easily separate and come through prongs of a dinner fork?",
          answers: [
            { label: "Yes", result: WizardStepAnswerResult.Proceed, nextStepIndex: 5 },
            { label: "No", result: WizardStepAnswerResult.TestFail }
          ]
        },
        {
          question: "Does the food hold its shape on the teaspoon?",
          answers: [
            { label: "Yes", result: WizardStepAnswerResult.Proceed, nextStepIndex: 6 },
            { label: "No", result: WizardStepAnswerResult.TestFail }
          ]
        },
        {
          question:
            "Tilt the spoon to its side and give it a gentle flick from the wrist. Does the food slide off with little left on teaspoon (i.e. not sticky)?",
          answers: [
            { label: "Yes", result: WizardStepAnswerResult.TestSuccess },
            { label: "No", result: WizardStepAnswerResult.TestFail }
          ]
        },
        {
          question:
            "Does the food consist of small, soft, smooth, rounded particles that can be easily squashed between fingers?",
          answers: [
            { label: "Yes", result: WizardStepAnswerResult.Proceed, nextStepIndex: 8 },
            { label: "No", result: WizardStepAnswerResult.TestFail }
          ]
        },
        {
          question: "Does the food feel moist and leave your fingers wet?",
          answers: [
            { label: "Yes", result: WizardStepAnswerResult.TestSuccess },
            { label: "No", result: WizardStepAnswerResult.TestFail }
          ]
        }
      ]
    ],
    [
      TextureLevel.SB6,
      [
        {
          question: "Are the pieces of food smaller than 15mm x 15mm?",
          answers: [
            { label: "Yes", result: WizardStepAnswerResult.Proceed, nextStepIndex: 1 },
            { label: "No", result: WizardStepAnswerResult.TestFail }
          ]
        },
        {
          question: "Does the food hold together without a thin liquid pooling?",
          answers: [
            { label: "Yes", result: WizardStepAnswerResult.Proceed, nextStepIndex: 2 },
            { label: "No", result: WizardStepAnswerResult.TestFail }
          ]
        },
        {
          question: "Do you have access to a metal dinner fork <strong>or</strong> a teaspoon?",
          answers: [
            { label: "Yes", result: WizardStepAnswerResult.Proceed, nextStepIndex: 3 },
            { label: "No", result: WizardStepAnswerResult.Proceed, nextStepIndex: 5 }
          ]
        },
        {
          question:
            "Push down on a 15mm x 15mm sample with a dinner fork or teaspoon, with enough pressure that your thumb nail turns white. Does the sample squash without returning to its original shape?",
          answers: [
            { label: "Yes", result: WizardStepAnswerResult.Proceed, nextStepIndex: 4 },
            { label: "No", result: WizardStepAnswerResult.TestFail }
          ]
        },
        {
          question:
            "Can the food be separated into smaller pieces using pressure from a dinner fork or teaspoon held on its side?",
          answers: [
            { label: "Yes", result: WizardStepAnswerResult.TestSuccess },
            { label: "No", result: WizardStepAnswerResult.TestFail }
          ]
        },
        {
          question:
            "Push down on a 15mm x 15mm sample using your thumb, with enough pressure that the thumb nail turns white. Does the sample squash without returning to its original shape?",
          answers: [
            { label: "Yes", result: WizardStepAnswerResult.Proceed, nextStepIndex: 6 },
            { label: "No", result: WizardStepAnswerResult.TestFail }
          ]
        },
        {
          question: "Does the food feel moist?",
          answers: [
            { label: "Yes", result: WizardStepAnswerResult.TestSuccess },
            { label: "No", result: WizardStepAnswerResult.TestFail }
          ]
        }
      ]
    ],
    [
      TextureLevel.EC7,
      [
        {
          question: "Do you have access to a metal dinner fork <strong>or</strong> a teaspoon?",
          answers: [
            { label: "Yes", result: WizardStepAnswerResult.Proceed, nextStepIndex: 1 },
            { label: "No", result: WizardStepAnswerResult.Proceed, nextStepIndex: 3 }
          ]
        },
        {
          question: "Can you break the food apart easily with the side of the dinner fork or spoon?",
          answers: [
            { label: "Yes", result: WizardStepAnswerResult.Proceed, nextStepIndex: 2 },
            { label: "No", result: WizardStepAnswerResult.TestFail }
          ]
        },
        {
          question:
            "Push down on a 15mm x 15mm sample with a dinner fork or teaspoon, with enough pressure that your thumb nail turns white. Does the sample squash without returning to its original shape?",
          answers: [
            { label: "Yes", result: WizardStepAnswerResult.TestSuccess },
            { label: "No", result: WizardStepAnswerResult.TestFail }
          ]
        },
        {
          question: "Can you break the food apart easily with your fingers?",
          answers: [
            { label: "Yes", result: WizardStepAnswerResult.Proceed, nextStepIndex: 4 },
            { label: "No", result: WizardStepAnswerResult.TestFail }
          ]
        },
        {
          question:
            "Push down on a 15mm x 15mm sample using your thumb, with enough pressure that the thumb nail turns white. Does the sample squash without returning to its original shape?",
          answers: [
            { label: "Yes", result: WizardStepAnswerResult.Proceed, nextStepIndex: 5 },
            { label: "No", result: WizardStepAnswerResult.TestFail }
          ]
        },
        {
          question: "Does the food feel moist?",
          answers: [
            { label: "Yes", result: WizardStepAnswerResult.TestSuccess },
            { label: "No", result: WizardStepAnswerResult.TestFail }
          ]
        }
      ]
    ]
  ])
}
